/**
 * The Segment events a Studio chat turn sends, and the properties on them.
 *
 * `model_chat_message_sent` is the name admin's model editor chat already sends
 * (mk-admin-app `ANALYTICS_EVENTS.MODEL_CHAT_MESSAGE_SENT`), so one Amplitude chart
 * covers both apps and the `app` property tells them apart.
 *
 * `session_id` is the thread id this panel mints and hands to `startTurn`. rgip
 * records the same string as the LangSmith trace's `metadata.session_id`, so it is
 * the key that joins a click in Amplitude to the agent turn it started. It exists
 * before the first turn, so even a conversation's first message joins.
 *
 * The page is read once, when the message is sent, and set on both events: the
 * Segment middleware (`studioEventDefaults`) would otherwise stamp the completion
 * with wherever the user is by the time the agent answers.
 *
 * Never the message itself: only its length.
 */

import { pageProperties } from '../../../utils/analytics-page';

export const CHAT_MESSAGE_SENT = 'model_chat_message_sent';
export const CHAT_RESPONSE_COMPLETED = 'model_chat_response_completed';

/** Where the chat lives: the model page rail, or the models list ("Ask Data Studio"). */
export type ChatSurface = 'model_page' | 'home';

export type ChatTurnState = 'success' | 'error' | 'aborted';

/** The page a turn was sent from (`pageProperties`). */
export type ChatPage = ReturnType<typeof pageProperties>;

export type ChatTracker = (
  eventName: string,
  properties: Record<string, unknown>
) => void;

export interface ChatTurn {
  /** The tenant being worked on, from the URL. */
  tenantId: string;
  /** Null for a chat whose subject is the tenant rather than one model. */
  modelId: string | null;
  agent: string;
  sessionId: string;
  surface: ChatSurface;
}

// `tenant` is the tenant being WORKED ON, as on Studio's page events, and it
// replaces the one the store would add. Admin's events carry the user's home tenant
// under that name, which is the reverse, so a cross-app chart reads the org from
// `path` on admin's side.
function turnProperties(turn: ChatTurn, page: ChatPage) {
  return {
    tenant: turn.tenantId,
    model_id: turn.modelId,
    agent: turn.agent,
    session_id: turn.sessionId,
    surface: turn.surface,
    ...page,
  };
}

export function messageSentProperties(
  turn: ChatTurn,
  page: ChatPage,
  message: string
) {
  return { ...turnProperties(turn, page), message_length: message.length };
}

export function responseCompletedProperties(
  turn: ChatTurn,
  page: ChatPage,
  outcome: {
    /** Null when the turn failed before rgip handed back a run id. */
    runId: string | null;
    state: ChatTurnState;
    durationMs: number;
  }
) {
  return {
    ...turnProperties(turn, page),
    run_id: outcome.runId,
    state: outcome.state,
    duration_ms: outcome.durationMs,
  };
}

/**
 * Send an event without letting the tracker break the turn. The calls sit before a
 * turn's `try` and inside its `finally`, where a throw would leave the composer
 * stuck on "asking".
 */
export function trackSafely(
  tracker: ChatTracker | undefined,
  eventName: string,
  properties: Record<string, unknown>
): void {
  try {
    tracker?.(eventName, properties);
  } catch {
    // Analytics only; the turn carries on.
  }
}

/**
 * How a turn ended. A stop wins over a failure, because stopping aborts the reader
 * and that surfaces as a failure on the way out.
 */
export function turnState(ended: {
  stopped: boolean;
  failed: boolean;
}): ChatTurnState {
  if (ended.stopped) return 'aborted';
  return ended.failed ? 'error' : 'success';
}

/**
 * Both events for one turn. Sends `model_chat_message_sent` now and returns what the
 * turn calls as it goes: `ran` once rgip hands back a run id, `ended` from its
 * `finally`, which sends `model_chat_response_completed` with the duration since now.
 */
export function trackTurn(
  tracker: ChatTracker | undefined,
  turn: ChatTurn,
  message: string,
  {
    now = Date.now,
    pathname = () => globalThis.location?.pathname ?? '',
  }: { now?: () => number; pathname?: () => string } = {}
) {
  const startedAt = now();
  const page = pageProperties(pathname());
  let runId: string | null = null;
  trackSafely(
    tracker,
    CHAT_MESSAGE_SENT,
    messageSentProperties(turn, page, message)
  );
  return {
    ran(id: string) {
      runId = id;
    },
    ended(ended: { stopped: boolean; failed: boolean }) {
      trackSafely(
        tracker,
        CHAT_RESPONSE_COMPLETED,
        responseCompletedProperties(turn, page, {
          runId,
          state: turnState(ended),
          durationMs: now() - startedAt,
        })
      );
    },
  };
}
